import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { JobKind, JobStatus } from '@algoboo/schema'
import type { AdminJob } from '@algoboo/schema/api'
import { Button, Pill, Select, Skeleton, type PillTone, fullTime, timeAgo } from '@algoboo/ui'
import { adminJobsQuery, adminKeys, jobActions, useAdminJobs } from '../lib/admin'
import { errorCopy, isApiError, useApiMutation } from '../lib/api'
import { useSession } from '../components/session'
import { AdminError, MoreButton, Table, Td } from '../components/admin/parts'
import { prefetchSignedIn } from '../lib/route-data'

const Search = z.object({ status: JobStatus.optional(), kind: JobKind.optional() })

export const Route = createFileRoute('/admin/jobs')({
  validateSearch: Search,
  loaderDeps: ({ search }) => ({ status: search.status, kind: search.kind }),
  loader: ({ context: { queryClient: qc }, deps }) => prefetchSignedIn(qc, (me) => [me.isAdmin !== false && qc.ensureInfiniteQueryData(adminJobsQuery(deps.status, deps.kind))]),
  component: AdminJobsPage,
})

const STATUS_TONE: Record<AdminJob['status'], PillTone> = { queued: 'info', running: 'brand', succeeded: 'good', failed: 'warn', dead: 'bad', cancelled: 'neutral' }

function AdminJobsPage() {
  const { me } = useSession()
  const s = Route.useSearch()
  const navigate = useNavigate({ from: '/admin/jobs' })
  const q = useAdminJobs(s.status, s.kind, !!me)
  const [error, setError] = useState<string | null>(null)
  const opts = {
    invalidate: () => [adminKeys.root()],
    onSuccess: () => setError(null),
  }
  const retry = useApiMutation('adminRetryJob', opts)
  const cancel = useApiMutation('adminCancelJob', opts)
  const fail = (e: unknown) => setError(isApiError(e) ? `${errorCopy(e.code).title}: ${e.message}` : String(e))
  const items = q.data?.pages.flatMap((p) => p.items) ?? []
  const set = (patch: Partial<z.infer<typeof Search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3" data-testid="admin-jobs-filters">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Status</span>
          <Select aria-label="Job status" value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as AdminJob['status'] | undefined })}>
            <option value="">Any status</option>
            {JobStatus.options.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Kind</span>
          <Select aria-label="Job kind" value={s.kind ?? ''} onChange={(e) => set({ kind: (e.target.value || undefined) as z.infer<typeof JobKind> | undefined })}>
            <option value="">Any kind</option>
            {JobKind.options.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </label>
        {(s.status || s.kind) && (
          <Button variant="ghost" size="sm" onClick={() => void navigate({ search: {}, replace: true })}>
            Clear
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="rounded-[8px] border border-line bg-danger-soft px-3 py-2 text-[13px] text-ink">
          {error}
        </p>
      )}
      {q.isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : q.error ? (
        <AdminError error={q.error} refetch={() => void q.refetch()} />
      ) : (
        <>
          <Table head={['Kind', 'Status', 'Attempts', 'Tenant', 'Updated', 'Last error', '']} testId="admin-jobs" caption="Jobs, most recently changed first">
            {items.length === 0 && (
              <tr>
                <Td className="py-6 text-ink-3">No jobs match.</Td>
              </tr>
            )}
            {items.map((j) => {
              const a = jobActions(j.status)
              return (
                <tr key={j.id} data-testid="admin-job" data-job-id={j.id} data-status={j.status}>
                  <Td mono title={j.id}>
                    {j.kind}
                  </Td>
                  <Td>
                    <Pill tone={STATUS_TONE[j.status]}>{j.status}</Pill>
                  </Td>
                  <Td mono>
                    {j.attempts}/{j.maxAttempts}
                  </Td>
                  <Td mono className="max-w-[140px] truncate text-ink-3" title={j.tenantKey}>
                    {j.tenantKey}
                  </Td>
                  <Td mono className="whitespace-nowrap text-ink-3">
                    <time dateTime={j.updatedAt} title={`updated ${fullTime(j.updatedAt)} · run at ${fullTime(j.runAt)}`}>
                      {timeAgo(j.updatedAt)}
                    </time>
                  </Td>
                  <Td className="max-w-[320px] truncate text-[12.5px] text-ink-2" title={j.lastError ?? undefined}>
                    {j.lastError ?? <span className="text-ink-4">—</span>}
                  </Td>
                  <Td className="whitespace-nowrap text-right">
                    {a.retry && (
                      <Button size="sm" variant="secondary" icon="refresh" loading={retry.isPending && retry.variables?.params.id === j.id} onClick={() => retry.mutate({ params: { id: j.id } }, { onError: fail })}>
                        Retry
                      </Button>
                    )}
                    {a.cancel && (
                      <Button size="sm" variant="ghost" icon="close" loading={cancel.isPending && cancel.variables?.params.id === j.id} onClick={() => cancel.mutate({ params: { id: j.id } }, { onError: fail })}>
                        Cancel
                      </Button>
                    )}
                  </Td>
                </tr>
              )
            })}
          </Table>
          <MoreButton hasNext={q.hasNextPage} loading={q.isFetchingNextPage} onMore={() => void q.fetchNextPage()} />
        </>
      )}
    </div>
  )
}
